import { useEffect, RefObject, useRef } from 'react';

interface UseFocusManagementOptions {
  isOpen: boolean;
  containerRef: RefObject<HTMLElement | null>;
  onEscape?: () => void;
  autoFocus?: boolean;
}

/**
 * Custom hook for managing focus within modal/dialog components
 * Provides escape key handling and focus trapping functionality
 */
export const useFocusManagement = ({
  isOpen,
  containerRef,
  onEscape,
  autoFocus = true,
}: UseFocusManagementOptions) => {
  const hasAutoFocusedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasAutoFocusedRef.current = false;
      return;
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onEscape?.();
      }
    };

    const handleTab = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const focusableElements = containerRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0] as HTMLElement;
          const lastElement = focusableElements[
            focusableElements.length - 1
          ] as HTMLElement;

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTab);

    // Auto-focus first element only once when modal opens
    if (autoFocus && !hasAutoFocusedRef.current) {
      hasAutoFocusedRef.current = true;
      setTimeout(() => {
        const firstInput = containerRef.current?.querySelector(
          'input, textarea, button'
        ) as HTMLElement;
        firstInput?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen, containerRef, onEscape, autoFocus]);
};